/*
 * Browser-local inbox filter store.
 *
 * Schema is co-maintained with the inbox-components package; bump
 * `INBOX_FILTERS_SCHEMA_VERSION` in both places on shape change.
 *
 * Two independent LS entries: `zignal-inbox-filters` for the user's applied
 * state and `zignal-inbox-filter-additions` for side-flow additions merged
 * into the effective filter set at read time.
 */

export const INBOX_FILTERS_LS_KEY = 'zignal-inbox-filters';
export const INBOX_FILTER_ADDITIONS_LS_KEY = 'zignal-inbox-filter-additions';
export const INBOX_FILTERS_SCHEMA_VERSION = 1;
export const INBOX_FILTER_STORAGE_EVENT = 'zignal-inbox-filter-storage-changed';

const createDefaultState = () => ({
  version: INBOX_FILTERS_SCHEMA_VERSION,
  detectAiStreams: [],
  newsroomAlerts: [],
  reportsEnabled: true,
});

const createDefaultAdditions = () => ({
  version: INBOX_FILTERS_SCHEMA_VERSION,
  newsroomAlerts: [],
  newsroomAlertNames: {},
});

const asStringDict = (value) => {
  if (!value || typeof value !== 'object') {return {};}
  const result = {};
  Object.keys(value).forEach((k) => {
    if (typeof value[k] === 'string') {result[k] = value[k];}
  });
  return result;
};

const resolveStorage = (storage) => storage || (typeof localStorage !== 'undefined' ? localStorage : null);

const asStringArray = (value) => {
  if (!Array.isArray(value)) {return [];}
  return value.filter((v) => typeof v === 'string');
};

const dispatchStorageChanged = () => {
  if (typeof window === 'undefined') {return;}
  window.dispatchEvent(new CustomEvent(INBOX_FILTER_STORAGE_EVENT));
};

export const hasStoredInboxFilterState = (storage) => {
  const store = resolveStorage(storage);
  if (!store) {return false;}
  try {
    const raw = store.getItem(INBOX_FILTERS_LS_KEY);
    if (!raw) {return false;}
    const parsed = JSON.parse(raw);
    return !!parsed && typeof parsed === 'object';
  } catch (_e) {
    return false;
  }
};

export const getInboxFilterState = (storage) => {
  const store = resolveStorage(storage);
  if (!store) {return createDefaultState();}
  try {
    const raw = store.getItem(INBOX_FILTERS_LS_KEY);
    if (!raw) {return createDefaultState();}
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {return createDefaultState();}
    return {
      version: typeof parsed.version === 'number' ? parsed.version : INBOX_FILTERS_SCHEMA_VERSION,
      detectAiStreams: asStringArray(parsed.detectAiStreams),
      newsroomAlerts: asStringArray(parsed.newsroomAlerts),
      reportsEnabled: typeof parsed.reportsEnabled === 'boolean' ? parsed.reportsEnabled : true,
    };
  } catch (_e) {
    return createDefaultState();
  }
};

// Throws — callers must catch (propagates from storage.setItem).
export const setInboxFilterState = (partial, storage) => {
  const store = resolveStorage(storage);
  if (!store) {return;}
  const current = getInboxFilterState(store);
  const next = {
    version: INBOX_FILTERS_SCHEMA_VERSION,
    detectAiStreams: partial.detectAiStreams != null ? partial.detectAiStreams : current.detectAiStreams,
    newsroomAlerts: partial.newsroomAlerts != null ? partial.newsroomAlerts : current.newsroomAlerts,
    reportsEnabled: partial.reportsEnabled != null ? partial.reportsEnabled : current.reportsEnabled,
  };
  store.setItem(INBOX_FILTERS_LS_KEY, JSON.stringify(next));
  dispatchStorageChanged();
};

export const getInboxFilterAdditions = (storage) => {
  const store = resolveStorage(storage);
  if (!store) {return createDefaultAdditions();}
  try {
    const raw = store.getItem(INBOX_FILTER_ADDITIONS_LS_KEY);
    if (!raw) {return createDefaultAdditions();}
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {return createDefaultAdditions();}
    return {
      version: typeof parsed.version === 'number' ? parsed.version : INBOX_FILTERS_SCHEMA_VERSION,
      newsroomAlerts: asStringArray(parsed.newsroomAlerts),
      newsroomAlertNames: asStringDict(parsed.newsroomAlertNames),
    };
  } catch (_e) {
    return createDefaultAdditions();
  }
};

// Throws — callers must catch.
export const setInboxFilterAdditions = (partial, storage) => {
  const store = resolveStorage(storage);
  if (!store) {return;}
  const current = getInboxFilterAdditions(store);
  const nextAlerts = partial.newsroomAlerts != null ? partial.newsroomAlerts : current.newsroomAlerts;
  // Names persist independently of the auto-check queue so an uncheck+Apply
  // does not remove the row from the ZEN list while pigeon is still catching
  // up. Cleanup is driven by cleanupAdditionsAfterBootstrap once pigeon
  // returns the real record, or an explicit removeNewsroomAlertFromDefaultView.
  const nextNames = partial.newsroomAlertNames != null
    ? { ...partial.newsroomAlertNames }
    : { ...(current.newsroomAlertNames || {}) };
  const next = {
    version: INBOX_FILTERS_SCHEMA_VERSION,
    newsroomAlerts: nextAlerts,
    newsroomAlertNames: nextNames,
  };
  store.setItem(INBOX_FILTER_ADDITIONS_LS_KEY, JSON.stringify(next));
  dispatchStorageChanged();
};

// Throws — callers must catch (propagates from setInboxFilterAdditions).
export const addNewsroomAlertToDefaultView = (alertId, alertName, storage) => {
  if (!alertId) {return false;}
  // Backward-compat: old callers pass (alertId) or (alertId, storage).
  let name = alertName;
  let store = storage;
  if (name != null && typeof name === 'object' && typeof name.getItem === 'function') {
    store = name;
    name = null;
  }
  store = resolveStorage(store);
  if (!store) {return false;}
  const additions = getInboxFilterAdditions(store);
  const alreadyPresent = additions.newsroomAlerts.indexOf(alertId) !== -1;
  const existingName = additions.newsroomAlertNames && additions.newsroomAlertNames[alertId];
  if (alreadyPresent && (!name || existingName === name)) {return false;}
  const nextNames = { ...(additions.newsroomAlertNames || {}) };
  if (typeof name === 'string' && name.length > 0) {
    nextNames[alertId] = name;
  }
  setInboxFilterAdditions(
    {
      newsroomAlerts: alreadyPresent
        ? additions.newsroomAlerts
        : [...additions.newsroomAlerts, alertId],
      newsroomAlertNames: nextNames,
    },
    store,
  );
  return true;
};

/*
 * Throws — callers must catch (propagates from setItem).
 *
 * Ensures the alert is NOT part of the user's Inbox filter without erasing
 * the display-name metadata. Called from the "No, thanks" path on the
 * auto-enable modal so a previously-checked alert becomes unchecked. Does
 * NOT create applied state when it does not exist — that would collapse
 * implicit defaults for the other filter fields.
 */
export const disableNewsroomAlertInDefaultView = (alertId, alertName, storage) => {
  if (!alertId) {return false;}
  // Backward-compat: allow (alertId, storage) two-arg form.
  let name = alertName;
  let store = storage;
  if (name != null && typeof name === 'object' && typeof name.getItem === 'function') {
    store = name;
    name = null;
  }
  store = resolveStorage(store);
  if (!store) {return false;}
  let changed = false;
  if (hasStoredInboxFilterState(store)) {
    const state = getInboxFilterState(store);
    if (state.newsroomAlerts.indexOf(alertId) !== -1) {
      setInboxFilterState({ newsroomAlerts: state.newsroomAlerts.filter((id) => id !== alertId) }, store);
      changed = true;
    }
  }
  const additions = getInboxFilterAdditions(store);
  const inQueue = additions.newsroomAlerts.indexOf(alertId) !== -1;
  const existingName = additions.newsroomAlertNames && additions.newsroomAlertNames[alertId];
  const shouldStoreName = typeof name === 'string' && name.length > 0 && existingName !== name;
  if (inQueue || shouldStoreName) {
    const nextNames = { ...(additions.newsroomAlertNames || {}) };
    if (shouldStoreName) {nextNames[alertId] = name;}
    setInboxFilterAdditions(
      {
        newsroomAlerts: inQueue
          ? additions.newsroomAlerts.filter((id) => id !== alertId)
          : additions.newsroomAlerts,
        newsroomAlertNames: nextNames,
      },
      store,
    );
    changed = true;
  }
  return changed;
};

// Throws — callers must catch.
export const removeNewsroomAlertFromDefaultView = (alertId, storage) => {
  if (!alertId) {return false;}
  const store = resolveStorage(storage);
  if (!store) {return false;}
  let changed = false;
  const state = getInboxFilterState(store);
  if (state.newsroomAlerts.indexOf(alertId) !== -1) {
    setInboxFilterState({ newsroomAlerts: state.newsroomAlerts.filter((id) => id !== alertId) }, store);
    changed = true;
  }
  const additions = getInboxFilterAdditions(store);
  const names = additions.newsroomAlertNames || {};
  const hadName = Object.prototype.hasOwnProperty.call(names, alertId);
  const hadInQueue = additions.newsroomAlerts.indexOf(alertId) !== -1;
  if (hadInQueue || hadName) {
    const nextNames = { ...names };
    delete nextNames[alertId];
    setInboxFilterAdditions(
      {
        newsroomAlerts: additions.newsroomAlerts.filter((id) => id !== alertId),
        newsroomAlertNames: nextNames,
      },
      store,
    );
    changed = true;
  }
  return changed;
};

/*
 * Attached on module import to match the existing `window.Views`/`window.Util`
 * pattern in `coffee/globals.coffee`, so CoffeeScript views can read/write
 * without ES-module plumbing.
 */
if (typeof window !== 'undefined') {
  window.InboxFilterStorage = {
    INBOX_FILTERS_LS_KEY,
    INBOX_FILTER_ADDITIONS_LS_KEY,
    INBOX_FILTER_STORAGE_EVENT,
    INBOX_FILTERS_SCHEMA_VERSION,
    addNewsroomAlertToDefaultView,
    disableNewsroomAlertInDefaultView,
    getInboxFilterAdditions,
    getInboxFilterState,
    hasStoredInboxFilterState,
    removeNewsroomAlertFromDefaultView,
    setInboxFilterAdditions,
    setInboxFilterState,
  };
}
